import React, { useSyncExternalStore } from 'react';

import { showViewingMode } from '../../../../../../viewer/view-scenes';
import { ViewSettings } from '../../../../../../viewer/view-settings';
import { useFeatureFlag } from '../../../../../hooks/use-feature-flag';
import { ThemeManager } from '../../../../../themes/theme-manager';
import { SkyviewContext } from '../../../../skyview/skyview-state';

/**
 * Returns the view settings, re-rendering when they change.
 */
export function useViewSettings() {
  return useSyncExternalStore(ViewSettings.instance.subscribe, ViewSettings.instance.getSnapshot);
}

/**
 * Shows the viewing mode when the view settings are turned on or off in the flags manager: the
 * classic mode when they are turned off, and the chosen viewing mode again when they are turned on.
 * The chosen viewing mode is kept. Shared views keep the classic view they were saved with.
 */
export function useSceneFollowsViewSettingsFlag() {
  const { activeScene } = React.useContext(SkyviewContext);
  const enabled = useFeatureFlag('SK-7549-view-settings');
  const previous = React.useRef({ enabled, viewingMode: ViewSettings.instance.viewingMode });

  React.useEffect(() => {
    // In the panorama viewer, the change waits until the scene it returns to is shown.
    if (previous.current.enabled === enabled || activeScene === 'panoramaViewer') {
      return;
    }

    const previousViewingMode = previous.current.viewingMode;
    previous.current = { enabled, viewingMode: ViewSettings.instance.viewingMode };
    // The CAD viewer has no layers to show by the viewing mode
    if (
      ThemeManager.instance.isSharedView ||
      activeScene !== 'skyviewScene' ||
      ViewSettings.instance.isCadViewer
    ) {
      return;
    }

    showViewingMode(previousViewingMode);
  }, [activeScene, enabled]);
}
